// src/services/apiService.js

/**
 * A centralized API service that handles all fetch requests for the application.
 * It automatically handles authentication logic (redirects on 401),
 * JSON (de)serialization, and provides .get/.post/.put/.patch/.delete helpers.
 */
const DEFAULT_OPTS = {
    credentials: 'include',
};

async function request(url, {method = 'GET', body, headers = {}, ...rest} = {}) {
    const opts = {...DEFAULT_OPTS, method, headers: {...headers}, ...rest};

    if (body instanceof FormData) {
        opts.body = body;
        // CRITICAL: We DO NOT set the 'Content-Type' header for FormData.
        // The browser must do this automatically to include the boundary.
    } else if (body !== undefined && body !== null) {
        opts.headers['Content-Type'] = 'application/json';
        opts.body = JSON.stringify(body);
    }

    let res;
    try {
        res = await fetch(url, opts);
    } catch (err) {
        console.error(`Network error when calling ${method} ${url}:`, err);
        throw new Error('Network error');
    }

    if (res.status === 401) {
        // Simply throw an error. Do not redirect.
        // The UI (AuthGuard/AuthContext) will catch this.
        throw new Error('User is not authenticated.');
    }

    // other HTTP errors
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error(`API error ${res.status} on ${method} ${url}:`, text);
        const error = new Error(text || `API request failed with status ${res.status}`);
        error.status = res.status;
        throw error;
    }

    // No Content
    if (res.status === 204 || res.headers.get('Content-Length') === '0') {
        return null;
    }

    // Try JSON, fallback to text
    const ct = res.headers.get('Content-Type') || '';
    if (ct.includes('application/json')) {
        return res.json();
    } else {
        return res.text();
    }
}

export default {
    get: (url, opts) => request(url, {method: 'GET', ...opts}),
    post: (url, body, opts) => request(url, {method: 'POST', body, ...opts}),
    put: (url, body, opts) => request(url, {method: 'PUT', body, ...opts}),
    patch: (url, body, opts) => request(url, {method: 'PATCH', body, ...opts}),
    delete: (url, opts) => request(url, {method: 'DELETE', ...opts}),
};